import { create } from 'zustand';
import type { ConfigResponse, UserDto } from '@chromarble/shared-types';
import { ApiClientError, api, errorMessage, setAuthToken, setUnauthorizedHandler } from './api';
import { rememberReturnUrl } from './crew-bridge';
import { type RealtimeClient, createRealtimeClient } from './realtime';
import { serverClock } from './server-clock';

const TOKEN_KEY = 'chromarble.session';
const AFTER_LOGIN_KEY = 'chromarble.afterLogin';

interface StoredSession {
  token: string;
  expiresAt: number;
  sessionId: string;
}

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    const s = raw ? (JSON.parse(raw) as StoredSession) : null;
    return s && s.expiresAt > Date.now() + 30_000 ? s : null;
  } catch {
    return null;
  }
}

function writeStored(s: StoredSession | null): void {
  try {
    if (s) sessionStorage.setItem(TOKEN_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable: session lives in memory only */
  }
}

/** Where to go after login (e.g. an invite link opened before signing in). Same-origin paths only. */
export function setAfterLogin(path: string): void {
  if (path.startsWith('/') && !path.startsWith('//')) sessionStorage.setItem(AFTER_LOGIN_KEY, path);
}
export function takeAfterLogin(): string | null {
  const p = sessionStorage.getItem(AFTER_LOGIN_KEY);
  sessionStorage.removeItem(AFTER_LOGIN_KEY);
  return p && p.startsWith('/') && !p.startsWith('//') ? p : null;
}

interface SessionState {
  status: 'booting' | 'anonymous' | 'authenticated';
  config: ConfigResponse | null;
  user: UserDto | null;
  sessionId: string | null;
  realtime: RealtimeClient | null;
  error: string | null;
  boot(): Promise<void>;
  devLogin(tenantId: string, crewUserId: string, nickname: string): Promise<void>;
  logout(): void;
}

async function establish(token: string, expiresAt: number, sessionId: string, config: ConfigResponse) {
  setAuthToken(token);
  writeStored({ token, expiresAt, sessionId });
  const realtime = await createRealtimeClient(config, token);
  return realtime;
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'booting',
  config: null,
  user: null,
  sessionId: null,
  realtime: null,
  error: null,

  async boot() {
    setUnauthorizedHandler(() => get().logout());
    let config: ConfigResponse;
    try {
      config = await api.config();
    } catch (e) {
      set({ status: 'anonymous', error: `서버에 연결할 수 없습니다: ${errorMessage(e)}` });
      return;
    }
    set({ config });
    void serverClock.sync();

    // Crew launch: credential arrives in the URL fragment (never sent to servers / Referer).
    const hash = new URLSearchParams(location.hash.slice(1));
    const credential = hash.get('ticket') ?? hash.get('code');
    const query = new URLSearchParams(location.search);
    rememberReturnUrl(hash.get('return') ?? query.get('return'));
    if (credential) {
      history.replaceState(null, '', location.pathname + location.search);
      try {
        const ex = await api.exchange(credential);
        const realtime = await establish(ex.token, ex.expiresAt, ex.sessionId, config);
        set({ status: 'authenticated', user: ex.user, sessionId: ex.sessionId, realtime, error: null });
        return;
      } catch (e) {
        set({ error: `Crew 인증에 실패했습니다: ${errorMessage(e)}` });
      }
    }

    const stored = readStored();
    if (stored) {
      setAuthToken(stored.token);
      try {
        const me = await api.me();
        const realtime = await establish(stored.token, stored.expiresAt, stored.sessionId, config);
        set({ status: 'authenticated', user: me.user, sessionId: stored.sessionId, realtime });
        return;
      } catch (e) {
        if (!(e instanceof ApiClientError && e.isNetwork)) writeStored(null);
        setAuthToken(null);
      }
    }
    set({ status: 'anonymous' });
  },

  async devLogin(tenantId, crewUserId, nickname) {
    const config = get().config;
    if (!config) throw new Error('config not loaded');
    const { code } = await api.devLogin(tenantId, crewUserId, nickname);
    const ex = await api.exchange(code);
    get().realtime?.close();
    const realtime = await establish(ex.token, ex.expiresAt, ex.sessionId, config);
    set({ status: 'authenticated', user: ex.user, sessionId: ex.sessionId, realtime, error: null });
  },

  logout() {
    get().realtime?.close();
    writeStored(null);
    setAuthToken(null);
    set({ status: 'anonymous', user: null, sessionId: null, realtime: null });
  },
}));
